/**
 * Sub-projeto 4 Lote 1 — Banner inline pra preparar tenant antes de
 * listar zonas Route53 no Step 1 do wizard.
 *
 * Comportamento:
 *   1. Quando ``selectedTenant.ztna_provision_role_arn`` é ``null``,
 *      Step1TenantDns renderiza este banner em vez do dropdown de zonas.
 *   2. Click "Preparar agora" chama ``POST /api/v1/admin/ztna/tenants/{id}/prepare``
 *      com timeout de 35s (~10s assume + ~10s create + buffer).
 *   3. Em sucesso (200 fresh ou ``already_prepared=true``), invoca
 *      ``onPrepared(role_arn)`` — caller deve invalidar a query de tenants.
 *   4. Em erro (4xx/5xx), exibe ``classification`` + mensagem com link
 *      pro runbook manual de fallback.
 */
import { Alert, Anchor, Button, Stack, Text } from '@mantine/core'
import { IconAlertTriangle, IconExternalLink } from '@tabler/icons-react'
import { useState } from 'react'
import axios from 'axios'
import { ztnaApi } from '../api'
import { PRECISA_DE_RUNBOOK, prepareErrorMessage } from './prepareErrors'

interface Props {
  tenantId: number
  tenantName: string
  onPrepared: (roleArn: string) => void
  /**
   * 'preparar' = a role nao existe na conta (coluna nula).
   * 'reparo'  = a role existe e o assume falhou agora.
   *
   * O modo de reparo existe porque o banner so aparecia com a coluna nula — e o
   * caso em que NAO havia banner era justamente o caso em que o acesso estava
   * quebrado. A mensagem de erro das zonas mandava "rode Preparar acima", e o
   * botao nao estava na tela (TASK-9892).
   */
  modo?: 'preparar' | 'reparo'
}

interface PrepareError {
  classification: string
  message: string
}

const RUNBOOK_URL =
  '/docs/superpowers/runbooks/2026-05-08-ztna-tenant-onboard-prerequisites.md'

export function TenantPrepareBanner({
  tenantId,
  tenantName,
  onPrepared,
  modo = 'preparar',
}: Props) {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<PrepareError | null>(null)
  // Escrita feita e acesso nao confirmado: nem sucesso nem falha. Terceiro
  // estado de proposito — colapsar nos dois anteriores e o defeito deste card.
  const [naoConfirmado, setNaoConfirmado] = useState<string | null>(null)

  const handlePrepare = async () => {
    setLoading(true)
    setError(null)
    setNaoConfirmado(null)
    try {
      const resp = await ztnaApi.prepareTenant(tenantId)
      if (resp.verified === false) {
        setNaoConfirmado(resp.verification_error ?? 'unknown')
        return
      }
      onPrepared(resp.role_arn)
    } catch (err: unknown) {
      let classification = 'unknown'
      let message = 'Falha desconhecida ao preparar o tenant.'
      if (axios.isAxiosError(err)) {
        const detail = err.response?.data?.detail
        if (detail && typeof detail === 'object') {
          classification = String(detail.classification ?? 'unknown')
          message = String(detail.error ?? err.message)
        } else if (typeof detail === 'string') {
          message = detail
        } else {
          message = err.message
        }
      } else if (err instanceof Error) {
        message = err.message
      }
      setError({ classification, message })
    } finally {
      setLoading(false)
    }
  }

  const reparo = modo === 'reparo'

  return (
    <Alert
      icon={<IconAlertTriangle size={20} />}
      color="yellow"
      title={
        reparo
          ? `A plataforma não conseguiu assumir a role em ${tenantName}`
          : `Tenant ${tenantName} ainda não preparado para ZTNA`
      }
      data-testid="tenant-prepare-banner"
    >
      <Stack gap="xs">
        <Text size="sm">
          {reparo ? (
            <>
              A <code>ConsoleAIZtnaProvisionRole</code> existe nessa conta, mas o
              acesso falhou agora. Reparar reescreve a permissão de confiança da
              role e confere o acesso em seguida (~10s).
            </>
          ) : (
            <>
              Vamos criar a role <code>ConsoleAIZtnaProvisionRole</code> na conta
              tenant automaticamente (~10s). Após preparar, as zonas Route53
              ficam disponíveis pro próximo passo.
            </>
          )}
        </Text>
        {naoConfirmado && (
          <Alert
            color="yellow"
            variant="light"
            icon={<IconAlertTriangle size={16} />}
            data-testid="prepare-nao-confirmado"
          >
            <Stack gap={4}>
              <Text size="sm">{prepareErrorMessage(naoConfirmado)}</Text>
              <Text size="xs" c="dimmed">
                {naoConfirmado}
              </Text>
            </Stack>
          </Alert>
        )}
        {error && (
          <Alert
            color="red"
            variant="light"
            icon={<IconAlertTriangle size={16} />}
            data-testid="prepare-error"
          >
            <Stack gap={4}>
              <Text size="sm">{prepareErrorMessage(error.classification)}</Text>
              {PRECISA_DE_RUNBOOK.has(error.classification) && (
                <Anchor
                  href={RUNBOOK_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  size="sm"
                >
                  Ver runbook manual <IconExternalLink size={12} />
                </Anchor>
              )}
              <Text size="xs" c="dimmed">
                {error.classification} — {error.message}
              </Text>
            </Stack>
          </Alert>
        )}
        <Button
          loading={loading}
          onClick={handlePrepare}
          data-testid="prepare-button"
          variant="filled"
          color="yellow"
        >
          {reparo ? 'Reparar agora' : 'Preparar agora'}
        </Button>
      </Stack>
    </Alert>
  )
}
